import type { NextApiRequest, NextApiResponse } from 'next';
import { supabaseAdmin } from '@/lib/supabaseAdmin';

/**
 * GET /api/ai-insights/get?scope=global|cliente&target_id=...&tipo=resumen,alerta
 * 
 * Returns cached AI insights from Supabase.
 * No auth required on the API level — RLS handles access control.
 * But we validate the bearer token to know who's asking.
 */
export default async function handler(req: NextApiRequest, res: NextApiResponse) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  const { scope, target_id, tipo } = req.query;

  if (!scope || typeof scope !== 'string') {
    return res.status(400).json({ error: 'scope requerido (global|asesor|cliente)' });
  }

  try {
    let query = supabaseAdmin
      .from('ai_dashboard_insights')
      .select('*')
      .eq('scope', scope)
      .eq('activo', true)
      .order('generado_en', { ascending: false });

    if (target_id && typeof target_id === 'string') {
      query = query.eq('target_id', target_id);
    }

    if (tipo && typeof tipo === 'string') {
      const tipos = tipo.split(',').map(t => t.trim());
      query = query.in('tipo', tipos);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching AI insights:', error);
      return res.status(500).json({ error: error.message });
    }

    // Group by tipo for easier consumption
    const grouped: Record<string, any[]> = {};
    for (const row of (data || [])) {
      if (!grouped[row.tipo]) grouped[row.tipo] = [];
      grouped[row.tipo].push(row);
    }

    return res.status(200).json({
      insights: data || [],
      grouped,
      generado_en: data?.[0]?.generado_en || null,
    });
  } catch (err: any) {
    console.error('AI insights get error:', err);
    return res.status(500).json({ error: err.message || 'Error interno' });
  }
}
